import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { SignOut, User, Wrench, Globe, CaretDown, List, X } from "@phosphor-icons/react";
import { LANGS } from "../i18n";
import SimCardIcon from "./SimCardIcon";

function LangMenu() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];
  const pick = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
    setOpen(false);
  };
  return (
    <div className="relative" ref={ref} data-testid="lang-menu">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-ink-700 hover:text-ink-950 rounded-full hover:bg-cobalt-50"
        data-testid="lang-menu-trigger"
      >
        <Globe size={16} weight="bold" /> {current.label}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white border border-ink-200 rounded-2xl shadow-soft overflow-hidden z-50" data-testid="lang-menu-list">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium flex items-center justify-between hover:bg-cobalt-50 ${i18n.language === l.code ? "text-cobalt-600" : "text-ink-700"}`}
              data-testid={`lang-${l.code}`}
            >
              <span>{l.name}</span>
              <span className="text-xs opacity-60">{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobile, setMobile] = useState(false);

  // "Pay & Fly" nav target: authenticated → their wallet; guest → the on-home explainer.
  // Avoids duplicating the "Log in" CTA (which would otherwise redirect here).
  const payflyHref = user ? "/payfly" : "/#payfly";

  return (
    <header className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl border-b border-ink-200/70" data-testid="header">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <div className="w-9 h-9 bg-cobalt-500 rounded-full flex items-center justify-center shadow-soft">
            <SimCardIcon size={18} className="text-white" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">{t("common.brand")}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 text-sm font-semibold">
          <NavLink to="/plans" className={({ isActive }) => `px-4 py-2 rounded-full ${isActive ? "bg-cobalt-50 text-cobalt-600" : "text-ink-700 hover:bg-cobalt-50 hover:text-cobalt-600"}`} data-testid="nav-plans">
            {t("nav.plans")}
          </NavLink>
          {user ? (
            <NavLink to="/payfly" className={({ isActive }) => `px-4 py-2 rounded-full inline-flex items-center gap-1 ${isActive ? "bg-coral-soft/60 text-coral-deep" : "text-ink-700 hover:bg-coral-soft/40 hover:text-coral-deep"}`} data-testid="nav-payfly">
              <span className="text-coral">⚡</span> {t("common.payfly")}
            </NavLink>
          ) : (
            <a href={payflyHref} className="px-4 py-2 rounded-full inline-flex items-center gap-1 text-ink-700 hover:bg-coral-soft/40 hover:text-coral-deep" data-testid="nav-payfly">
              <span className="text-coral">⚡</span> {t("common.payfly")}
            </a>
          )}
          <NavLink to="/calculator" className={({ isActive }) => `px-4 py-2 rounded-full ${isActive ? "bg-cobalt-50 text-cobalt-600" : "text-ink-700 hover:bg-cobalt-50 hover:text-cobalt-600"}`} data-testid="nav-calculator">
            {t("common.calculator")}
          </NavLink>
          <a href="/#faq" className="px-4 py-2 rounded-full text-ink-700 hover:bg-cobalt-50 hover:text-cobalt-600" data-testid="nav-faq">
            FAQ
          </a>
          {user?.role === "admin" && (
            <NavLink to="/admin" className="px-4 py-2 rounded-full text-ink-700 hover:bg-cobalt-50 inline-flex items-center gap-1" data-testid="nav-admin">
              <Wrench size={14} /> {t("common.admin")}
            </NavLink>
          )}
        </nav>
        <div className="flex items-center gap-1.5">
          <LangMenu />
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:inline-flex btn-ghost !py-2 !px-3 text-sm" data-testid="nav-dashboard">
                <User size={14} weight="bold" /> {t("common.dashboard")}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} className="btn-ghost !py-2 !px-3 text-sm" data-testid="btn-logout">
                <SignOut size={14} /> <span className="hidden sm:inline">{t("common.logout")}</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block btn-ghost !py-2 !px-3 text-sm" data-testid="nav-login">
                {t("nav.login")}
              </Link>
              <Link to="/register" className="btn-primary !py-2 !px-4 text-sm" data-testid="nav-signup">
                {t("nav.signup")}
              </Link>
            </>
          )}
          <button onClick={() => setMobile(!mobile)} className="md:hidden btn-ghost !p-2" data-testid="mobile-menu-toggle">
            {mobile ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>
      {mobile && (
        <div className="md:hidden border-t border-ink-200 bg-white" data-testid="mobile-menu">
          <div className="px-5 py-4 space-y-2 text-sm font-semibold">
            <Link to="/plans" className="block py-2" onClick={() => setMobile(false)}>{t("nav.plans")}</Link>
            <a href="/#how" className="block py-2" onClick={() => setMobile(false)}>{t("nav.howItWorks")}</a>
            <a href="/#faq" className="block py-2" onClick={() => setMobile(false)}>FAQ</a>
            {!user && <Link to="/login" className="block py-2" onClick={() => setMobile(false)}>{t("nav.login")}</Link>}
          </div>
        </div>
      )}
    </header>
  );
}
